import { DEFAULT_LINE_SYMBOL, IkeToken, type LineSymbol } from '@ikegps/core-api';
import { CollectionModelFieldLine } from '@ikegps/core-shared';
import { lineSymbolDecor, type LineSymbolDecor, lineSymbolStrokeNodes } from './line.symbol.render';
import type { MapPointDragger } from './map.point.drag';
import { rgba } from './theme';
import { WHITE } from './theme.palette';
import { T, Translate } from '../i18n';
import { m, type MithrilVNode } from '../mithril';
import { GenericMapPoint } from '../modules/topological.editor.map/map.point.generic';
import { Services } from '../services';
import { classNames } from '../util/classnames';
import type { LineDrawState } from '../state/line.draw.state';
import { CurrentState } from '../state/page.state';

/** CSS class names a surface owns for its line-field polylines (kept per-surface so print-capture
 * can keep selecting the topo classes by name). */
export interface LineFieldPolylineClasses {
	container: string;
	vector: string;
	highlight: string;
	clickTarget: string;
}

// Resting-satellite halo tuning. On aerial imagery the white halo sits faint and slightly wider than the
// base hover halo so dark lines stay legible. Shared by the saved-line CSS and the live draw preview.
const HALO_BASE_PAD = 2; // px added to the line weight for the base (hover) halo
const HALO_SATELLITE_PAD = 3; // px added for the wider resting-satellite halo
const HALO_SATELLITE_OPACITY = 0.55;

export function lineFieldPolylineStyles(c: LineFieldPolylineClasses) {
	return {
		[c.container]: {
			pointerEvents: 'none',
			transform: 'translate3d(0, 0, -1px)',
			[`&:hover .${c.highlight}`]: {
				opacity: 1,
			},
			// On satellite/imagery the white halo rests slightly wider and faded so dark lines stay legible;
			// hover (handled by the rule above) snaps opacity to full and shrinks it back to the base width.
			[`&.is-satellite:not(:hover) .${c.highlight}`]: {
				opacity: HALO_SATELLITE_OPACITY,
				strokeWidth: 'var(--halo-rest-w)',
			},
		},
		[c.clickTarget]: {
			cursor: 'pointer',
			pointerEvents: 'stroke',
		},
		[c.highlight]: {
			opacity: 0,
			transition: 'opacity 150ms ease, stroke-width 150ms ease',
		},
	};
}

// One <polyline> per visual layer (rather than N separate <line>s) so corners join cleanly
// (stroke-linejoin) with no wedge gaps at interior vertices. `gaps` knocks short breaks out of
// the coloured stroke so same-coloured labels stay legible; highlight and click target stay whole.
function segmentNodes(
	pixels: google.maps.Point[],
	sym: LineSymbol,
	gaps: Array<[number, number]>,
	classes: LineFieldPolylineClasses,
	onClick?: () => void
): MithrilVNode[] {
	const points = pixels.map((p) => `${p.x},${p.y}`).join(' ');

	const nodes: MithrilVNode[] = [
		m('polyline', {
			key: 'hl',
			className: classes.highlight,
			points,
			fill: 'none',
			stroke: rgba(WHITE),
			'stroke-width': `${sym.weight + HALO_BASE_PAD}px`,
			'stroke-linejoin': 'round',
			'stroke-linecap': 'round',
			// Resting-satellite halo width (1px wider than the base); shrinks back to the base on hover.
			style: `--halo-rest-w:${sym.weight + HALO_SATELLITE_PAD}px`,
		}),
		...lineSymbolStrokeNodes(pixels, sym, gaps, 'p'),
	];

	if (onClick) {
		nodes.push(
			m('polyline', {
				key: 'ct',
				className: classes.clickTarget,
				points,
				fill: 'none',
				stroke: 'rgba(0,0,0,0)',
				'stroke-width': '12px',
				'stroke-linejoin': 'round',
				onclick: onClick,
			})
		);
	}

	return nodes;
}

/** Faint white resting halo behind the live preview on satellite/imagery, mirroring the saved-line resting
 * halo so dark lines stay legible while drawing. */
function drawPreviewHalo(
	pixels: google.maps.Point[],
	sym: LineSymbol,
	decor: LineSymbolDecor,
	key: string
): MithrilVNode {
	const points = pixels.map((p) => `${p.x},${p.y}`).join(' ');
	return m('g', { key: `${key}-halo`, opacity: HALO_SATELLITE_OPACITY }, [
		m('polyline', {
			key: `${key}-halo-line`,
			points,
			fill: 'none',
			stroke: rgba(WHITE),
			'stroke-width': `${sym.weight + HALO_SATELLITE_PAD}px`,
			'stroke-linejoin': 'round',
			'stroke-linecap': 'round',
		}),
		...decor.highlightNodes,
	]);
}

/** Live draw-preview `<svg>` (no hover halo / click target), shared by every surface so the line type
 * previews identically while drawing. */
export function lineDrawPreviewSvg(pixels: google.maps.Point[], sym: LineSymbol, key: string): MithrilVNode | null {
	if (pixels.length < 2) {
		return null;
	}
	const decor = lineSymbolDecor(pixels, sym, key);
	return m(
		'svg',
		{
			key: `${key}-symbol`,
			style: { position: 'absolute', left: '0px', top: '0px', overflow: 'visible', pointerEvents: 'none' },
		},
		[
			...(Services.Map.isSatellite ? [drawPreviewHalo(pixels, sym, decor, key)] : []),
			...lineSymbolStrokeNodes(pixels, sym, decor.gaps, key),
			...decor.nodes,
		]
	);
}

/** Draw nodes (preview line + a draggable/removable dot per vertex), shared by every surface; the only
 * per-surface input is how a vertex projects to div-pixels. */
export function lineDrawNodes(
	draw: LineDrawState,
	dragger: MapPointDragger,
	project: (location: google.maps.LatLng) => google.maps.Point | null,
	previewKey: string
): MithrilVNode[] {
	const points = draw.points;
	if (points.length === 0) {
		return [];
	}

	// Pending type (chosen up-front, not yet on the field) wins so mid-draw restyles preview live.
	const field = draw.targetField;
	const sym = field
		? (field.schema.symbolForTitle(draw.pendingLineType) ??
			field.schema.symbolForTitle(field.lineType) ??
			DEFAULT_LINE_SYMBOL)
		: DEFAULT_LINE_SYMBOL;

	const projected = points.map((p) => project(p.location));
	const pixels = projected.filter((p): p is google.maps.Point => p != null);

	const nodes: MithrilVNode[] = [];
	const preview = lineDrawPreviewSvg(pixels, sym, previewKey);
	if (preview) {
		nodes.push(preview);
	}

	points.forEach((point, index) => {
		const pixel = projected[index];
		if (pixel == null) {
			return;
		}
		nodes.push(
			dragger.pointNode({
				state: draw,
				point,
				pixel,
				keyPrefix: 'line-draw',
				onClick: (e: Event) => {
					e.stopPropagation();
					draw.removePoint(point.id);
				},
				title: Translate(T.MEASUREMENT_CLICK_TO_REMOVE),
			})
		);
	});

	return nodes;
}

/** Renders a saved line as a styled SVG polyline. Surface-neutral: the caller supplies the projection
 * and CSS classes, keeping this a pure renderer. */
export function lineFieldPolylineNodes(
	field: CollectionModelFieldLine,
	classes: LineFieldPolylineClasses,
	project: (lat: number, lng: number) => google.maps.Point | null,
	onClick?: () => void
): MithrilVNode | null {
	// Token gate stays in the shared renderer (not the call sites): print-capture scrapes these nodes from
	// the DOM, so gating here is what hides saved lines uniformly on every surface, print included.
	if (!Services.Token.has(IkeToken.FEATURE_LINE_TOOL)) {
		return null;
	}

	const symbol = field.schema.symbolForTitle(field.lineType) ?? DEFAULT_LINE_SYMBOL;
	const pixels = field.points
		.map((p) => project(p.latitude, p.longitude))
		.filter((p): p is google.maps.Point => p != null);

	if (pixels.length < 2) {
		return null;
	}

	const decor = lineSymbolDecor(pixels, symbol, 'p');

	// White decoration halos share the highlight class so they fade in with the line underlay on hover.
	const decorHighlight =
		decor.highlightNodes.length > 0
			? m('g', { key: 'hl-decor', className: classes.highlight }, decor.highlightNodes)
			: null;

	return m(
		'div',
		{ className: classNames(classes.container, { 'is-satellite': Services.Map.isSatellite }) },
		[
			m(
				'svg',
				{
					key: 'vector',
					className: classes.vector,
					style: { overflow: 'visible', position: 'absolute' },
				},
				[...segmentNodes(pixels, symbol, decor.gaps, classes, onClick), decorHighlight, ...decor.nodes]
			),
			focusedVertexNode(field, project),
		].filter(Boolean)
	);
}

/** White dot (same symbology as the draw dots) marking the vertex whose form accordion is open
 * (UFO-969). Display-only: lines can't be edited in place, so it re-asserts the container's
 * pointer-events:none rather than wiring any handlers. */
function focusedVertexNode(
	field: CollectionModelFieldLine,
	project: (lat: number, lng: number) => google.maps.Point | null
): MithrilVNode | null {
	const focus = CurrentState.focusedLineVertex;
	const isFocused =
		focus != null && focus.collectionRevision === field.collection.revision && focus.fieldId === field.id;
	const point = isFocused ? field.points[focus.index] : null;
	const pixel = point != null ? project(point.latitude, point.longitude) : null;
	if (pixel == null) {
		return null;
	}
	return GenericMapPoint.view({
		key: 'focused-vertex',
		point: pixel,
		backgroundColor: rgba(WHITE),
		additionalStyles: { pointerEvents: 'none' },
	});
}
